import { Skeleton } from '../../../platform/async/Skeleton'
import type { AsyncState } from '../../../platform/async/Async'
import { Meter } from '../../../platform/primitives/Meter'
import { Money } from '../../../platform/primitives/Money'
import { ACCOUNTS_PATH } from '../../accounts/accountPaths'
import type { NetWorth } from '../../accounts/useNetWorth'
import { OverviewWidget } from '../OverviewWidget'
import { assetShare } from './assetShare'

/**
 * The hero. Every figure is one `useNetWorth` already returns — M5's own
 * `totalsOf`, so this is the number `/accounts` shows. No sparkline or delta
 * chip: each needs balance history or a new sum (intake A1–A3). The assets-vs-debt
 * meter is drawing only (`assetShare`), hidden when there is nothing to compare.
 * Mixed currency shows one line per currency and no single figure (D21).
 */
export function NetWorthWidget({ state }: { state: AsyncState<NetWorth> }) {
  return (
    <OverviewWidget<NetWorth>
      title="Net worth"
      subtitle="All accounts · today"
      href={ACCOUNTS_PATH}
      hero
      surface="overview-net-worth"
      state={state}
      skeleton={
        <>
          <Skeleton height={36} width={180} />
          <Skeleton height={13} width={140} />
        </>
      }
      empty={{ title: 'No accounts yet' }}
    >
      {({ totals, byCurrency }) => {
        const share = totals === null ? null : assetShare(totals.assetsCents, totals.liabilitiesCents)
        return totals !== null ? (
          <div className="ov-networth">
            <Money cents={totals.netWorthCents} currency={totals.currency ?? undefined} variant="hero" kind="stock" />
            <p className="ov-networth__split">
              <span>Assets </span>
              <Money cents={totals.assetsCents} currency={totals.currency ?? undefined} kind="stock" />
              <span> · Debt </span>
              <Money cents={totals.liabilitiesCents} currency={totals.currency ?? undefined} kind="stock" />
            </p>
            {share !== null && (
              <Meter
                skin="onAccent"
                className="ov-networth__meter"
                label="Assets share of assets and debt"
                value={share}
                valueText={`${share}% assets`}
              />
            )}
          </div>
        ) : (
          <ul className="ov-networth ov-networth__currencies">
            {byCurrency.map(({ currency, totals: t }) =>
              t === null ? null : (
                <li key={currency} className="ov-networth__currency">
                  <span className="ov-networth__code">{currency}</span>
                  <Money cents={t.netWorthCents} currency={currency} variant="hero" kind="stock" />
                </li>
              ),
            )}
          </ul>
        )
      }}
    </OverviewWidget>
  )
}
